import { useState, useEffect, memo } from 'react';
import { formatFranc } from '../lib/utils';
import api from '../lib/api';
import EntityAvatar from './EntityAvatar';
import { Skeleton } from '../components/ui/skeleton';
import {
  Star, Calendar
} from 'lucide-react';

const TYPE_COLORS = {
  country_leader: '#5a2d5e', ceo: '#1e3a5f', actor: '#c97a2e',
  athlete: '#2d7d5e', musician: '#b8860b', influencer: '#c94070',
};

export default memo(function WeeklyDigest({ onEntityClick }) {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/digest/weekly').then(res => {
      setDigest(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton className="h-40 w-full rounded-2xl" />;
  if (!digest) return null;

  const summary = digest.week_summary || {};
  const topPerformers = digest.top_performers || [];

  return (
    <div className="bg-card border border-border/60 rounded-2xl p-4" data-testid="weekly-digest">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-4 w-4 text-[hsl(var(--accent))]" />
        <h3 className="text-sm font-semibold">Weekly Digest</h3>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-secondary/30 rounded-lg p-2 text-center">
          <p className="text-[10px] text-muted-foreground">₣ Earned</p>
          <p className="text-base font-bold tabular-nums text-[hsl(var(--positive-text))]">
            {formatFranc(summary.total_points_earned, { sign: true })}
          </p>
        </div>
        <div className="bg-secondary/30 rounded-lg p-2 text-center">
          <p className="text-[10px] text-muted-foreground">People</p>
          <p className="text-base font-bold tabular-nums">{summary.entities_on_roster || 0}</p>
        </div>
        <div className="bg-secondary/30 rounded-lg p-2 text-center">
          <p className="text-[10px] text-muted-foreground">Leagues</p>
          <p className="text-base font-bold tabular-nums">{summary.leagues_count || 0}</p>
        </div>
      </div>

      {/* Top Performers */}
      {topPerformers.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            <Star className="h-3 w-3 inline mr-1" /> Top Performers
          </p>
          <div className="space-y-1">
            {topPerformers.map((entity, i) => {
              const canClick = !!(onEntityClick && entity.id);
              return (
                <div
                  key={entity.id || i}
                  className={`flex items-center justify-between px-2 py-1.5 rounded-lg bg-secondary/20 ${canClick ? 'cursor-pointer hover:bg-secondary/40 transition-colors' : ''}`}
                  onClick={canClick ? () => onEntityClick(entity.id) : undefined}
                  role={canClick ? 'button' : undefined}
                  tabIndex={canClick ? 0 : undefined}
                  onKeyDown={canClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEntityClick(entity.id); } } : undefined}
                  data-testid={canClick ? `top-performer-${entity.id}` : undefined}
                >
                  <div className="flex items-center gap-2">
                    <EntityAvatar entity={entity} size={24} />
                    <span className="text-xs font-medium">{entity.name}</span>
                  </div>
                  <span className="text-xs font-bold tabular-nums text-[hsl(var(--positive-text))]">
                    {formatFranc(entity.stats?.weekly_points, { sign: true })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});
